import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { requestService } from "@/services/RequestService";
import { usePrefetchUsers } from "@/services/hooks/userHooks";

/**
 * Hook for prefetching request-related data
 * Provides intelligent prefetching for request view pages
 */
export const useRequestPrefetch = (userId: number | null) => {
    const queryClient = useQueryClient();
    const { prefetchUsers } = usePrefetchUsers();

    /**
     * Prefetches request view details for a specific request
     */
    const prefetchRequestDetails = useCallback(
        async (requestId: number) => {
            if (!requestId || requestId <= 0 || !userId) return;

            try {
                await queryClient.prefetchQuery({
                    queryKey: ["requestViewDetails", requestId, userId],
                    queryFn: () =>
                        requestService.getRequestViewDetails(requestId, userId),
                    staleTime: 5 * 60 * 1000, // 5 minutes for request data
                });
            } catch (error) {
                console.warn(`Failed to prefetch request ${requestId}:`, error);
            }
        },
        [queryClient, userId]
    );

    /**
     * Prefetches user data commonly needed in request views
     */
    const prefetchRequestUsers = useCallback(async () => {
        try {
            await prefetchUsers();
        } catch (error) {
            console.warn("Failed to prefetch request users:", error);
        }
    }, [prefetchUsers]);

    /**
     * Prefetches request reference data (types, priorities, statuses)
     * These are already cached for 24 hours, so just ensure they're available
     */
    const prefetchRequestReferences = useCallback(async () => {
        try {
            // These are typically cached for 24 hours in the existing implementation
            // Just ensure they're in cache if needed
            const promises: Promise<unknown>[] = [];

            // Check if reference data is already cached, if not prefetch
            if (!queryClient.getQueryData(["lookup", "requestTypes"])) {
                // Add prefetch for request types if service exists
                // This would need to be implemented based on your lookup services
                // promises.push(queryClient.prefetchQuery(...));
            }

            if (!queryClient.getQueryData(["lookup", "requestPriorities"])) {
                // Add prefetch for request priorities if service exists
                // promises.push(queryClient.prefetchQuery(...));
            }

            if (!queryClient.getQueryData(["lookup", "requestStatuses"])) {
                // Add prefetch for request statuses if service exists
                // promises.push(queryClient.prefetchQuery(...));
            }

            await Promise.all(promises);
        } catch (error) {
            console.warn("Failed to prefetch request references:", error);
        }
    }, [queryClient]);

    /**
     * Prefetches all data needed for a request view page
     */
    const prefetchRequestView = useCallback(
        async (requestId: number) => {
            try {
                // Prefetch in parallel for better performance
                await Promise.all([
                    prefetchRequestDetails(requestId),
                    prefetchRequestUsers(),
                    prefetchRequestReferences(),
                ]);
            } catch (error) {
                console.warn(
                    `Request view prefetch failed for ${requestId}:`,
                    error
                );
            }
        },
        [
            prefetchRequestDetails,
            prefetchRequestUsers,
            prefetchRequestReferences,
        ]
    );

    /**
     * Batch prefetch multiple request details
     * Useful for prefetching visible requests in the list
     */
    const batchPrefetchRequests = useCallback(
        async (requestIds: number[]) => {
            try {
                // Limit to first 5 requests to avoid overwhelming the system
                const limitedIds = requestIds.slice(0, 5);

                // Prefetch common data once
                await Promise.all([
                    prefetchRequestUsers(),
                    prefetchRequestReferences(),
                ]);

                // Then prefetch individual request details
                const requestPromises = limitedIds.map((id) =>
                    prefetchRequestDetails(id)
                );
                await Promise.all(requestPromises);
            } catch (error) {
                console.warn("Batch request prefetch failed:", error);
            }
        },
        [
            prefetchRequestDetails,
            prefetchRequestUsers,
            prefetchRequestReferences,
        ]
    );

    return {
        prefetchRequestDetails,
        prefetchRequestUsers,
        prefetchRequestReferences,
        prefetchRequestView,
        batchPrefetchRequests,
    };
};
